import React, { SyntheticEvent, useCallback, useContext, useEffect } from 'react';
import { usePrevious } from 'hooks';
import { DealBuilderContext } from 'components/Builder/context';
import { BaseGroupItemPlaceholder, BaseGroupItem } from 'components/Builder/types';
import LineView from '../LineView';
import { DELAY_CLOSE_ON_ADD } from './constants';

export interface BuilderMobileLineProps {
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  name?: string;
  groupId: string;
  collapsed: boolean;
  completed: boolean;
  items: (BaseGroupItem | null)[];
}

const BuilderMobileLine = ({
  groupId,
  GroupItemPlaceholder,
  name,
  items,
  collapsed,
  completed,
}: BuilderMobileLineProps) => {
  const {
    dismissProductsScreen,
    setModalDismissFocusTargetRef,
    setSelectedGroup,
    setProductCountAndTotal,
  } = useContext(DealBuilderContext);

  const handleSlotClick = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      setSelectedGroup(groupId);
      setModalDismissFocusTargetRef(event.currentTarget);
    },
    [groupId, setModalDismissFocusTargetRef, setSelectedGroup],
  );

  const prevCompleted = usePrevious(completed);
  const justCompleted = completed && completed !== prevCompleted;

  const productCount = items.filter(i => i !== null).length;
  const prevProductCount = usePrevious(productCount);
  const initialised = prevProductCount !== undefined;

  const productAdded = initialised && productCount > 0 && productCount > prevProductCount;
  const productRemoved = initialised && productCount < prevProductCount;

  useEffect(() => {
    if (productAdded && setProductCountAndTotal) {
      setProductCountAndTotal({ totalCount: items.length, productCount });
    }
  }, [productAdded, setProductCountAndTotal, items.length, productCount]);

  useEffect(() => {
    if (productRemoved && setProductCountAndTotal) {
      setProductCountAndTotal(null);
    }
  }, [productRemoved, setProductCountAndTotal]);

  useEffect(
    () => () => {
      if (setProductCountAndTotal) {
        setProductCountAndTotal(null);
      }
    },
    [setProductCountAndTotal],
  );

  useEffect(() => {
    if (initialised && justCompleted) {
      setTimeout(dismissProductsScreen, DELAY_CLOSE_ON_ADD);
    }
  }, [initialised, justCompleted, dismissProductsScreen]);

  return (
    <LineView
      GroupItemPlaceholder={GroupItemPlaceholder}
      name={name}
      isMobile
      items={items}
      collapsed={collapsed}
      completed={completed}
      onSlotClick={handleSlotClick}
    />
  );
};

BuilderMobileLine.displayName = 'BuilderMobileLine';

export default BuilderMobileLine;
